import moment from 'moment';
import {EntityActivitiesRepository} from '../../../../../repositories/entity-activities';
import {EntityUsersRepository} from '../../../../../repositories/entity-users';
import {UsersRepository} from '../../../../../repositories/users';
import {
  BADL_SECTOR_ID,
  BADL__FOOD_ID,
  CLINICAL_SUPPORT_SECTOR_ID,
  CLINICAL_SUPPORT__MEDICATION_ID,
  CLINICAL_SUPPORT__VITAL_SIGNS_ID,
} from '../../../../constants';
import {exportCarePlanGetReportData} from '../../care-plan/base/export-care-plan-get-report-data';
import {getMealsActivityTypes} from '../../care-plan/clinical-support/medication/export-care-plan-medication-utils';
import {
  ExportSeniorProfileReportData,
  ExportSeniorProfileReportUserData,
} from './export-senior-profile-types';

/**
 * Fetches all necessary data for exporting a senior profile
 */
export async function exportSeniorProfileGetReportData({
  entityId,
  seniorId,
}: {
  entityId?: string;
  seniorId?: string;
}): Promise<ExportSeniorProfileReportData | null> {
  if (!entityId || !seniorId) {
    return null;
  }

  const userData = await fetchUserData(entityId, seniorId);

  // 1. Fetch all base data
  // Food data
  const foodReportData = await exportCarePlanGetReportData({
    entityId,
    senior: userData.user.user,
    activitiesSectorId: BADL_SECTOR_ID,
    activitiesGroupId: BADL__FOOD_ID,
  })
    .catch(() => undefined)
    .then((reportData) => reportData ?? undefined);

  // Medication data
  const medicationReportData = await exportCarePlanGetReportData({
    entityId,
    senior: userData.user.user,
    activitiesSectorId: CLINICAL_SUPPORT_SECTOR_ID,
    activitiesGroupId: CLINICAL_SUPPORT__MEDICATION_ID,
  })
    .catch(() => undefined)
    .then((reportData) => reportData ?? undefined);

  const [
    // Medication meal activity types
    mealsActivityTypesData,
    // Vital Signs activity types
    vitalSignsActivityTypes,
  ] = await Promise.all([
    getMealsActivityTypes(entityId).catch(() => []),
    EntityActivitiesRepository.instance.getAllTypes(
      entityId,
      CLINICAL_SUPPORT_SECTOR_ID,
      CLINICAL_SUPPORT__VITAL_SIGNS_ID
    ),
  ]);
  // 2. Done
  return {
    foodReportData,
    medicationReportData,
    mealsActivityTypesData,
    vitalSignsCarePlanData: {
      groupActivitiesTypesData: vitalSignsActivityTypes,
    },
    userData,
  };
}

// ------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------

async function fetchUserData(
  entityId: string,
  seniorId: string
): Promise<ExportSeniorProfileReportUserData> {
  const [user, healthProfile, latestVitalSigns] = await Promise.all([
    // User information (name, birthdate, etc.)
    EntityUsersRepository.instance.getById(entityId, seniorId),
    // Patologies information
    UsersRepository.instance.getUserHealthProfile(seniorId),
    // Vital Signs information (last X days)
    UsersRepository.instance.getAllUserHealthMeasurements({
      userId: seniorId,
      startDate: moment().utc().startOf('day').subtract(6, 'days').toISOString(),
      endDate: moment().utc().endOf('day').toISOString(),
    }),
  ]);

  return {
    user,
    healthProfile,
    latestHealthMeasurements: latestVitalSigns,
  };
}
